import { useAnnouncementBannerForm } from '../../hooks/useAnnouncementBannerForm'
import { useProductMessageForSettings } from '../../hooks/useProductMessageForSettings'
import { useLightMode } from '@/hooks/useLightMode'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  Button,
  DatePicker,
  Flex,
  Modal,
  MultilineTextInput,
  Select,
  Stack,
  Text,
  chakra,
} from '@hivewatch/dark'

import { SettingsGroup, settingsColorToken } from '@/features/settings'
import { MAX_ANNOUNCEMENT_MESSAGE_LENGTH } from '@/utils/forms/validation'

import {
  DEFAULT_MESSAGE_TYPE_OPTION,
  messageTypeOptions,
} from '../../constants/constants'
import { pmtToAlertString } from '../../utils/utils'
import { AnnouncementBannerSkeleton } from './AnnouncementBannerSkeleton'

export const AnnouncementBannerSection = () => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { currentMessage, isLoading, error } = useProductMessageForSettings()
  const { form, dates, handlers, state } = useAnnouncementBannerForm(
    currentMessage,
    { isRebrand: true }
  )

  const title = t('settings:rebrand_general_announcement_group_title')
  const primaryText = settingsColorToken(colorScheme, 'text.primary')
  const secondaryText = settingsColorToken(colorScheme, 'text.secondary')
  const errorTextColor = settingsColorToken(colorScheme, 'status.error-text')

  const fieldLabel = (label: string) => (
    <Text color={primaryText} textStyle='label'>
      {label}
    </Text>
  )

  if (error) {
    return (
      <SettingsGroup colorScheme={colorScheme} title={title}>
        <Text color={errorTextColor} textStyle='body'>
          {t('settings:rebrand_general_announcement_load_error')}
        </Text>
      </SettingsGroup>
    )
  }

  if (isLoading) {
    return (
      <SettingsGroup colorScheme={colorScheme} title={title}>
        <AnnouncementBannerSkeleton />
      </SettingsGroup>
    )
  }

  const selectedType =
    form.watch('productMessageType') ?? DEFAULT_MESSAGE_TYPE_OPTION
  const draftMessage: string = form.watch('message') ?? ''

  return (
    <SettingsGroup colorScheme={colorScheme} title={title}>
      <chakra.form onSubmit={form.handleSubmit(handlers.onCreateMessage)}>
        <Stack gap='16px' w='full'>
          <Stack gap='6px' w='full'>
            {fieldLabel(
              t('settings:rebrand_general_announcement_message_label')
            )}
            <Controller
              control={form.control}
              defaultValue=''
              name='message'
              render={({ value, onChange }) => (
                <MultilineTextInput
                  colorScheme={colorScheme}
                  data-testid='AnnouncementBanner:message'
                  hasWarning={!!form.errors.message}
                  isDisabled={state.isViewMode}
                  onChange={(e) => onChange(e.target.value)}
                  placeholder={t(
                    'settings:rebrand_general_announcement_message_placeholder'
                  )}
                  value={value ?? ''}
                  warningText={form.errors.message?.message}
                />
              )}
              rules={{
                maxLength: {
                  message: t(
                    'settings:rebrand_general_announcement_message_max_length'
                  ),
                  value: MAX_ANNOUNCEMENT_MESSAGE_LENGTH,
                },
                required: t(
                  'settings:rebrand_general_announcement_message_required'
                ),
                validate: (value) =>
                  value.trim().length > 0 ||
                  t('settings:rebrand_general_announcement_message_empty'),
              }}
            />
          </Stack>

          <Stack gap='6px' w='full'>
            {fieldLabel(t('settings:rebrand_general_announcement_type_label'))}
            <Controller
              control={form.control}
              defaultValue={DEFAULT_MESSAGE_TYPE_OPTION}
              name='productMessageType'
              render={({ value, onChange }) => (
                <Select
                  colorScheme={colorScheme}
                  data-testid='AnnouncementBanner:type'
                  isDisabled={state.isViewMode}
                  isSearchable={false}
                  onChange={onChange}
                  options={messageTypeOptions}
                  value={value ?? DEFAULT_MESSAGE_TYPE_OPTION}
                />
              )}
              rules={{ required: true }}
            />
          </Stack>

          {!state.isViewMode && (
            <Stack gap='6px' w='full'>
              {fieldLabel(
                t('settings:rebrand_general_announcement_preview_label')
              )}
              <Alert
                colorScheme={colorScheme}
                title={draftMessage}
                variant={pmtToAlertString(selectedType.value)}
              />
            </Stack>
          )}

          <Flex gap='16px' w='full'>
            <Box flex='1'>
              <DatePicker
                colorScheme={colorScheme}
                date={dates.startDate}
                isDisabled={state.isViewMode}
                label={t('settings:rebrand_general_announcement_start_date')}
                minDate={new Date()}
                onChange={dates.setStartDate}
              />
            </Box>
            <Box flex='1'>
              <DatePicker
                colorScheme={colorScheme}
                date={dates.startTime}
                dateFormat='h:mm aa'
                isDisabled={state.isViewMode}
                label={t('settings:rebrand_general_announcement_start_time')}
                onChange={dates.setStartTime}
                showTimeSelect
                showTimeSelectOnly
              />
            </Box>
          </Flex>

          <Flex gap='16px' w='full'>
            <Box flex='1'>
              <DatePicker
                colorScheme={colorScheme}
                date={dates.endDate}
                isDisabled={state.isViewMode}
                label={t('settings:rebrand_general_announcement_end_date')}
                minDate={dates.startDate}
                onChange={dates.setEndDate}
              />
            </Box>
            <Box flex='1'>
              <DatePicker
                colorScheme={colorScheme}
                date={dates.endTime}
                dateFormat='h:mm aa'
                isDisabled={state.isViewMode}
                label={t('settings:rebrand_general_announcement_end_time')}
                onChange={dates.setEndTime}
                showTimeSelect
                showTimeSelectOnly
              />
            </Box>
          </Flex>

          <Text color={secondaryText} fontStyle='italic' textStyle='caption'>
            {t('settings:rebrand_general_announcement_timezone_helper')}
          </Text>

          <Box>
            {state.isViewMode ? (
              <Button
                colorScheme={colorScheme}
                data-testid='AnnouncementBanner:delete'
                onClick={handlers.handleOpenDeleteModal}
                variant='danger'
              >
                {t('settings:rebrand_general_announcement_delete')}
              </Button>
            ) : (
              <Button
                colorScheme={colorScheme}
                data-testid='AnnouncementBanner:post'
                disabled={!state.isFormValid}
                isLoading={state.isCreating}
                type='submit'
                variant='primary'
              >
                {t('settings:rebrand_general_announcement_post')}
              </Button>
            )}
          </Box>
        </Stack>
      </chakra.form>

      <Modal
        colorScheme={colorScheme}
        isOpen={state.isDeleteModalOpen}
        isSaveDestructive
        isSaveLoading={state.isDeleting}
        onClose={handlers.handleCloseDeleteModal}
        onSave={handlers.onDeleteMessage}
        saveButtonLabel={t('settings:rebrand_general_announcement_delete')}
        showCancelButton
        size='compact'
        title={t('settings:rebrand_general_announcement_delete_title')}
      >
        <Text textStyle='body'>
          {t('settings:rebrand_general_announcement_delete_body')}
        </Text>
      </Modal>
    </SettingsGroup>
  )
}
